<script setup lang="ts">
import { z } from 'zod'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { LucideLoaderCircle } from 'lucide-vue-next'
import { PasswordToggleButton } from '~/components/ui/password-toggle-button'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form'
import { useToast } from '~/components/ui/toast'
import { useApiCall } from '~/composables/useApi'
const { t } = useI18n()
const router = useRouter()

// Pre-compute all validation messages to avoid calling useValidationMessage in computed
const validationMessages = {
  requiredFieldEmail: useValidationMessage('requiredFieldEmail'),
  emailInvalid: useValidationMessage('emailInvalid'),
  requiredFieldPassword: useValidationMessage('requiredFieldPassword'),
  formRegisterPasswordPlaceholder: useValidationMessage('form.register.password_placeholder'),
  messageCaptchaNotRobot: useValidationMessage('messageCaptchaNotRobot')
}

const resetPasswordSchema = z
  .object({
    email: z
      .string({ required_error: validationMessages.requiredFieldEmail })
      .email(validationMessages.emailInvalid)
      .min(1, validationMessages.requiredFieldEmail),
    password: z
      .string({ required_error: validationMessages.requiredFieldPassword })
      .min(8, t('form.register.password_placeholder')),
    password_confirmation: z
      .string({ required_error: validationMessages.requiredFieldPassword })
      .min(8, validationMessages.formRegisterPasswordPlaceholder)
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: t('common.passwords_mismatch'),
    path: ['password_confirmation']
  })

const { handleSubmit, meta, setFieldValue } = useForm({
  validationSchema: toTypedSchema(resetPasswordSchema)
})

const isLoading = ref(false)
const showPassword = ref(false)
const { toast } = useToast()

const route = useRoute()

// Get token from route parameters
const routeToken = computed(() => (route.params.token as string) || '')

// Extract email from URL query parameters and set it in the form
onMounted(() => {
  const emailFromQuery = route.query.email as string
  if (emailFromQuery) {
    const decodedEmail = decodeURIComponent(emailFromQuery)
    setFieldValue('email', decodedEmail)
  }
})

const onSubmit = handleSubmit(async (values) => {
  try {
    isLoading.value = true

    await useApiCall('/reset-password', {
      method: 'POST',
      body: {
        token: routeToken.value,
        email: values.email,
        password: values.password,
        password_confirmation: values.password_confirmation
      }
    })

    toast({
      title: t('feature.profile.password_change_form.change_success_title'),
      variant: 'success'
    })

    // Redirect to login immediately
    router.replace('/login')
  } catch (err: unknown) {
    const error = err as Error & { status?: number; data?: { message?: string } }
    let errorMessage = t('feature.profile.password_change_form.change_error_generic')

    if (error.status === 400 || error.status === 404) {
      errorMessage = t('feature.profile.password_change_form.change_error_title')
    } else if (error.status === 422) {
      errorMessage = validationMessages.messageCaptchaNotRobot
    } else if (error.data?.message && !error.data.message.includes('POST')) {
      errorMessage = error.data.message
    }

    toast({
      title: t('feature.profile.password_change_form.change_error_title'),
      description: errorMessage,
      variant: 'destructive'
    })
  } finally {
    isLoading.value = false
  }
})
</script>

<template>
  <div class="mx-auto w-full max-w-xl">
    <div class="space-y-5 rounded-2xl bg-white p-8 shadow-xl">
      <div class="space-y-2 text-center">
        <Heading tag="h1" class="AuthHeading">
          {{ t('feature.profile.password_change_form.heading') }}
        </Heading>
        <p class="text-gray-600">{{ t('feature.profile.password_change_form.subheading') }}</p>
      </div>

      <form class="space-y-6" @submit="onSubmit">
        <!-- Email -->
        <FormField v-slot="{ componentField }" name="email">
          <FormItem>
            <FormLabel>{{ t('common.email') }}*</FormLabel>
            <FormControl>
              <Input
                :placeholder="t('form.register.email_placeholder')"
                v-bind="componentField"
                :readonly="!!route.query.email"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <!-- New Password -->
        <FormField v-slot="{ componentField }" name="password">
          <FormItem>
            <FormLabel
              >{{ t('feature.profile.password_change_form.new_password_label') }}*</FormLabel
            >

            <div class="relative">
              <FormControl>
                <Input
                  :placeholder="t('feature.profile.password_change_form.new_password_placeholder')"
                  v-bind="componentField"
                  :type="showPassword ? 'text' : 'password'"
                  class="pr-12"
                />
              </FormControl>
              <PasswordToggleButton :show="showPassword" @toggle="showPassword = !showPassword" />
            </div>

            <FormMessage />
          </FormItem>
        </FormField>

        <!-- Confirm password -->
        <FormField v-slot="{ componentField }" name="password_confirmation">
          <FormItem>
            <FormLabel
              >{{ t('feature.profile.password_change_form.confirm_password_label') }}*</FormLabel
            >

            <div class="relative">
              <FormControl>
                <Input
                  :placeholder="
                    t('feature.profile.password_change_form.confirm_password_placeholder')
                  "
                  v-bind="componentField"
                  :type="showPassword ? 'text' : 'password'"
                  class="pr-12"
                />
              </FormControl>
              <PasswordToggleButton :show="showPassword" @toggle="showPassword = !showPassword" />
            </div>

            <FormMessage />
          </FormItem>
        </FormField>

        <Button type="submit" class="w-full" :disabled="isLoading || !meta.valid">
          <LucideLoaderCircle v-if="isLoading" class="mr-2 h-5 w-5 animate-spin" />
          {{
            isLoading
              ? t('feature.profile.password_change_form.sending_password')
              : t('feature.profile.password_change_form.change_password_button')
          }}
        </Button>
      </form>
    </div>
  </div>
</template>
